/**
 * Per-campaign minimap size in localStorage (flow view).
 * Legacy key `minimap-size` is migrated once to the first campaign that reads it.
 */

import {
  MINIMAP_SIZE_KEY,
  MINIMAP_SIZE_BY_CAMPAIGN_KEY,
  DEFAULT_MAX_WIDTH,
  DEFAULT_MAX_HEIGHT,
  MIN_SIZE,
  MAX_SIZE,
} from './constants';

function sanitizeDims(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const w = Number(raw.maxWidth);
  const h = Number(raw.maxHeight);
  if (!isFinite(w) || !isFinite(h) || w <= 0 || h <= 0) return null;
  return { maxWidth: w, maxHeight: h };
}

/** Legacy clamp used when reading old global key only */
function clampLegacyDims(maxWidth, maxHeight) {
  return {
    maxWidth: Math.max(MIN_SIZE, Math.min(MAX_SIZE, maxWidth || DEFAULT_MAX_WIDTH)),
    maxHeight: Math.max(MIN_SIZE, Math.min(MAX_SIZE, maxHeight || DEFAULT_MAX_HEIGHT)),
  };
}

function readMap() {
  try {
    const raw = localStorage.getItem(MINIMAP_SIZE_BY_CAMPAIGN_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

function writeMap(map) {
  try {
    localStorage.setItem(MINIMAP_SIZE_BY_CAMPAIGN_KEY, JSON.stringify(map));
  } catch (e) {
    console.warn('Failed to save minimap size map:', e);
  }
}

function readLegacyGlobal() {
  try {
    const stored = localStorage.getItem(MINIMAP_SIZE_KEY);
    if (!stored) return null;
    const { maxWidth, maxHeight } = JSON.parse(stored);
    return clampLegacyDims(maxWidth, maxHeight);
  } catch {
    return null;
  }
}

/**
 * @param {string} [campaignId]
 * @returns {{ maxWidth: number, maxHeight: number } | null}
 */
export function getMinimapSizeForCampaign(campaignId) {
  if (!campaignId) return null;

  const map = readMap();
  const existing = map[campaignId];
  const sanitized = sanitizeDims(existing);
  if (sanitized) return sanitized;

  // One-time migration: move legacy global size onto this campaign, then drop legacy key
  const legacy = readLegacyGlobal();
  if (legacy) {
    const next = { ...map, [campaignId]: legacy };
    writeMap(next);
    try {
      localStorage.removeItem(MINIMAP_SIZE_KEY);
    } catch {
      /* ignore */
    }
    return legacy;
  }

  return null;
}

/**
 * @param {string} campaignId
 * @param {number} maxWidth
 * @param {number} maxHeight
 */
export function setMinimapSizeForCampaign(campaignId, maxWidth, maxHeight) {
  if (!campaignId) return;
  const dims = sanitizeDims({ maxWidth, maxHeight });
  if (!dims) return;

  const map = readMap();
  map[campaignId] = dims;
  writeMap(map);
}

/**
 * Legacy global save (only when campaignId is unavailable)
 */
export function saveMinimapSizeGlobal(maxWidth, maxHeight) {
  try {
    const dims = clampLegacyDims(maxWidth, maxHeight);
    localStorage.setItem(MINIMAP_SIZE_KEY, JSON.stringify(dims));
  } catch (e) {
    console.warn('Failed to save minimap size:', e);
  }
}
